import { Constants, Utils } from "../utils";
import { useEffect, useState } from "react";
import { useStore } from "../store";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import MediumMultiDropdown from "./MediumMultiDropdown";
import Modal from "react-bootstrap/Modal";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

function Search({ onClick }: any) {
  const {
    keyword,
    setKeyword,
    priceRange,
    setPriceRange,
    mediumIds,
    setMediumIds,
    radius,
    setRadius,
  } = useStore((s: any) => s);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const [localKeyword, setLocalKeyword] = useState<string>(keyword || "");
  const debouncedKeyword = useDebouncedValue(localKeyword, 400);

  const [localPrice, setLocalPrice] = useState<[number, number]>([
    priceRange.min,
    priceRange.max,
  ]);
  const [localRadius, setLocalRadius] = useState<number>(radius);
  const [localMediumIds, setLocalMediumIds] = useState<string[]>(
    mediumIds || []
  );

  useEffect(() => {
    setLocalKeyword(keyword || "");
  }, [keyword]);

  useEffect(() => {
    setLocalPrice([priceRange.min, priceRange.max]);
  }, [priceRange.min, priceRange.max]);

  useEffect(() => {
    setLocalRadius(radius);
  }, [radius]);

  useEffect(() => {
    setLocalMediumIds(mediumIds || []);
  }, [mediumIds]);

  useEffect(() => {
    // Only push once the debounce has caught up to the current input.
    // Without the localKeyword guard, Clear all (which sets both
    // localKeyword and store keyword to "" synchronously) would race
    // this effect — the still-stale debouncedKeyword would fire one
    // last time and write the old value back to the store, which the
    // keyword→localKeyword sync below would then echo back into the
    // input, making the clear look like it didn't happen.
    if (
      debouncedKeyword === localKeyword &&
      debouncedKeyword !== keyword
    ) {
      setKeyword(debouncedKeyword);
    }
  }, [debouncedKeyword, localKeyword, keyword, setKeyword]);

  const activeFilterCount =
    (localPrice[0] !== Constants.PRICE_RANGE.min ||
    localPrice[1] !== Constants.PRICE_RANGE.max
      ? 1
      : 0) +
    (mediumIds?.length > 0 ? 1 : 0) +
    (localRadius !== Constants.SEARCH_MAX_RADIUS ? 1 : 0);

  const clearFilters = () => {
    setLocalKeyword("");
    setKeyword("");
    setLocalMediumIds([]);
    setMediumIds([]);
    setLocalPrice([Constants.PRICE_RANGE.min, Constants.PRICE_RANGE.max]);
    setPriceRange(Constants.PRICE_RANGE);
    setLocalRadius(Constants.SEARCH_MAX_RADIUS);
    setRadius(Constants.SEARCH_MAX_RADIUS);
    setShowFilters(false);
    onClick?.();
  };

  const applyFilters = () => {
    setPriceRange({ min: localPrice[0], max: localPrice[1] });
    setRadius(localRadius);
    setMediumIds(localMediumIds);
    setShowFilters(false);
    onClick?.();
  };

  return (
    <>
      <div className="searchPillMobile">
        <i className="bi bi-search searchPillMobile__icon"></i>
        <input
          type="text"
          placeholder="Search by artist, artwork, medium..."
          value={localKeyword}
          onChange={(e) => setLocalKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onClick?.();
            }
          }}
        />
        <button
          type="button"
          className="searchPillMobile__filterBtn"
          aria-label="Open filters"
          onClick={() => setShowFilters(true)}
        >
          <i className="bi bi-sliders"></i>
          {activeFilterCount > 0 && (
            <span className="searchPillMobile__filterCount">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <Modal
        show={showFilters}
        onHide={() => setShowFilters(false)}
        centered
        dialogClassName="filterModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="filterGroup">
            <div className="filterLabel">Keyword</div>
            <input
              type="text"
              className="filterKeywordInput"
              placeholder="Search by artist, artwork, medium..."
              value={localKeyword}
              onChange={(e) => setLocalKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyFilters();
                }
              }}
            />
          </div>

          <div className="filterGroup">
            <div className="filterLabel">Medium</div>
            <MediumMultiDropdown
              value={localMediumIds}
              onChange={setLocalMediumIds}
            />
          </div>

          <div className="filterGroup">
            <div className="filterLabel">
              Price range{" "}
              <span className="filterValue">
                {Utils.formatToCurrency(localPrice[0])} –{" "}
                {Utils.formatToCurrency(localPrice[1])}
                {localPrice[1] === 100000 && "+"}
              </span>
            </div>
            <Slider
              range
              value={localPrice}
              min={Constants.PRICE_RANGE.min}
              max={Constants.PRICE_RANGE.max}
              onChange={(w: any) => setLocalPrice([w[0], w[1]])}
              allowCross={false}
              step={100}
              trackStyle={[{ backgroundColor: "var(--brand-color)" }]}
              railStyle={{ backgroundColor: "var(--border-soft)" }}
            />
          </div>

          <div className="filterGroup">
            <div className="filterLabel">
              Distance{" "}
              <span className="filterValue">
                within {Utils.formatToNumber(localRadius.toString())}
                {localRadius === 500 && "+"} mi
              </span>
            </div>
            <Slider
              value={localRadius}
              min={Constants.SEARCH_MIN_RADIUS}
              max={Constants.SEARCH_MAX_RADIUS}
              onChange={(w: any) => setLocalRadius(w)}
              step={10}
              trackStyle={[{ backgroundColor: "var(--brand-color)" }]}
              railStyle={{ backgroundColor: "var(--border-soft)" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="filterModal__clearBtn"
            onClick={clearFilters}
          >
            Clear all
          </button>
          <button
            type="button"
            className="filterModal__applyBtn"
            onClick={applyFilters}
          >
            Apply filters
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Search;
